/**
 * The frame around the launch surfaces — missions, career, settings, how to
 * play (task 9.1). The title screen is deliberately outside it: it is the one
 * screen that is all identity and no navigation.
 */
import type { JSX, ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { AppLayout } from './AppLayout.js';
import { useActiveProfile } from '../store/view-store.js';

const LINKS = [
  { to: '/', label: 'Title', end: true },
  { to: '/missions', label: 'Missions', end: false },
  { to: '/career', label: 'Career', end: false },
  { to: '/how-to-play', label: 'How to Play', end: false },
  { to: '/settings', label: 'Settings', end: false },
] as const;

function GlobalNav(): JSX.Element {
  const profile = useActiveProfile();
  return (
    <nav aria-label="Sections" className="flex h-full flex-col gap-1 p-3">
      <p className="px-2 pb-2 text-xs font-semibold tracking-wide text-ink-subtle uppercase">
        Managing Up
      </p>
      {LINKS.map((link) => (
        <NavLink
          key={link.to}
          to={link.to}
          end={link.end}
          data-testid={`nav-${link.label.toLowerCase().replaceAll(' ', '-')}`}
          className={({ isActive }) =>
            `touch-target flex items-center rounded-lg px-3 py-2 text-sm no-underline ${
              isActive
                ? 'bg-accent-soft font-semibold text-ink'
                : 'text-ink-muted hover:bg-surface-muted hover:text-ink'
            }`
          }
        >
          {link.label}
        </NavLink>
      ))}
      {profile ? (
        <p className="mt-auto px-2 pt-4 text-xs text-ink-subtle">
          Current save: <span className="font-medium text-ink-muted">{profile.name}</span>
        </p>
      ) : null}
    </nav>
  );
}

export interface LaunchLayoutProps {
  readonly title: string;
  readonly children: ReactNode;
}

export function LaunchLayout({ title, children }: LaunchLayoutProps): JSX.Element {
  return (
    <AppLayout sidebar={<GlobalNav />} sidebarLabel="Sections" heading={title}>
      <div className="mx-auto w-full max-w-3xl px-4 py-6 sm:px-6">{children}</div>
    </AppLayout>
  );
}

/** The page's single `<h1>` plus its standfirst, styled once. */
export function PageHeading({
  title,
  lede,
}: {
  readonly title: string;
  readonly lede?: string | undefined;
}): JSX.Element {
  return (
    <header className="mb-6">
      <h1 className="text-2xl font-bold tracking-tight text-ink sm:text-3xl">{title}</h1>
      {lede ? <p className="mt-2 text-sm text-ink-muted sm:text-base">{lede}</p> : null}
    </header>
  );
}
